import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Button, 
  Typography, 
  Paper, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  TablePagination,
  IconButton,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  MenuItem,
  Grid,
  Tooltip,
  Tabs,
  Tab
} from '@mui/material';
import { 
  Delete as DeleteIcon, 
  Refresh as RefreshIcon,
  Email as EmailIcon,
  Telegram as TelegramIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon
} from '@mui/icons-material';
import { formatDistanceToNow } from 'date-fns';
import { ru } from 'date-fns/locale';
import axios from 'axios';

const SubscribersList = () => {
  const [tabValue, setTabValue] = useState(0);
  const [subscribers, setSubscribers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [subscriberToDelete, setSubscriberToDelete] = useState(null);
  
  useEffect(() => {
    fetchSubscribers();
  }, [tabValue, page, rowsPerPage, statusFilter, searchQuery]);

  const fetchSubscribers = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const subscriberType = tabValue === 0 ? 'email' : 'telegram';
      const isActive = statusFilter === 'active' ? true : 
                      statusFilter === 'inactive' ? false : undefined;
      
      const params = {
        page: page + 1,
        limit: rowsPerPage,
        isActive,
        search: searchQuery || undefined
      };
      
      const response = await axios.get(`/api/advertisements/subscribers/${subscriberType}`, { params });
      
      setSubscribers(response.data.subscribers);
      setTotalCount(response.data.totalItems || 0);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching subscribers:', err);
      setError(`Ошибка при загрузке ${tabValue === 0 ? 'email' : 'telegram'} подписчиков`);
      setLoading(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    setPage(0);
    setStatusFilter('all');
    setSearchQuery('');
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleStatusFilterChange = (event) => {
    setStatusFilter(event.target.value);
    setPage(0);
  };

  const handleSearchQueryChange = (event) => {
    setSearchQuery(event.target.value);
    setPage(0);
  };

  const handleDeleteClick = (subscriber) => {
    setSubscriberToDelete(subscriber);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!subscriberToDelete) return;
    
    try {
      const subscriberType = tabValue === 0 ? 'email' : 'telegram';
      await axios.delete(`/api/advertisements/subscribers/${subscriberType}/${subscriberToDelete.id}`);
      setDeleteDialogOpen(false);
      setSubscriberToDelete(null);
      fetchSubscribers();
    } catch (err) {
      console.error('Error deleting subscriber:', err);
      setError(`Ошибка при удалении ${tabValue === 0 ? 'email' : 'telegram'} подписчика`);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Нет данных';
    
    try {
      const date = new Date(dateString);
      return formatDistanceToNow(date, { addSuffix: true, locale: ru });
    } catch (error) {
      return dateString;
    }
  };

  const renderEmailSubscribersTable = () => {
    return (
      <TableContainer sx={{ maxHeight: 'calc(100vh - 300px)' }}>
        <Table stickyHeader aria-label="email subscribers table">
          <TableHead>
            <TableRow>
              <TableCell>Email</TableCell>
              <TableCell>Имя</TableCell>
              <TableCell>Статус</TableCell>
              <TableCell>Подписан</TableCell>
              <TableCell>Последняя активность</TableCell>
              <TableCell align="center">Действия</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  Загрузка...
                </TableCell>
              </TableRow>
            ) : subscribers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  Подписчики не найдены
                </TableCell>
              </TableRow>
            ) : (
              subscribers.map((subscriber) => (
                <TableRow key={subscriber.id} hover>
                  <TableCell>{subscriber.email}</TableCell>
                  <TableCell>{subscriber.name || 'Не указано'}</TableCell>
                  <TableCell>
                    {subscriber.isActive ? (
                      <Chip 
                        icon={<CheckCircleIcon />} 
                        label="Активен" 
                        color="success" 
                        size="small" 
                      />
                    ) : (
                      <Chip 
                        icon={<CancelIcon />} 
                        label="Неактивен" 
                        color="error" 
                        size="small" 
                      />
                    )}
                  </TableCell>
                  <TableCell>{formatDate(subscriber.createdAt)}</TableCell>
                  <TableCell>{formatDate(subscriber.lastActivity)}</TableCell>
                  <TableCell align="center">
                    <Tooltip title="Удалить">
                      <IconButton 
                        size="small"
                        onClick={() => handleDeleteClick(subscriber)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  const renderTelegramSubscribersTable = () => {
    return (
      <TableContainer sx={{ maxHeight: 'calc(100vh - 300px)' }}>
        <Table stickyHeader aria-label="telegram subscribers table">
          <TableHead>
            <TableRow>
              <TableCell>Имя</TableCell>
              <TableCell>Username</TableCell>
              <TableCell>Chat ID</TableCell>
              <TableCell>Статус</TableCell>
              <TableCell>Подписан</TableCell>
              <TableCell>Последняя активность</TableCell>
              <TableCell align="center">Действия</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  Загрузка...
                </TableCell>
              </TableRow>
            ) : subscribers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  Подписчики не найдены
                </TableCell>
              </TableRow>
            ) : (
              subscribers.map((subscriber) => (
                <TableRow key={subscriber.id} hover>
                  <TableCell>
                    {`${subscriber.firstName || ''} ${subscriber.lastName || ''}`.trim() || 'Не указано'}
                  </TableCell>
                  <TableCell>
                    {subscriber.username ? `@${subscriber.username}` : 'Не указано'}
                  </TableCell>
                  <TableCell>{subscriber.chatId}</TableCell>
                  <TableCell>
                    {subscriber.isActive ? (
                      <Chip 
                        icon={<CheckCircleIcon />} 
                        label="Активен" 
                        color="success" 
                        size="small" 
                      />
                    ) : (
                      <Chip 
                        icon={<CancelIcon />} 
                        label="Неактивен" 
                        color="error" 
                        size="small" 
                      />
                    )}
                  </TableCell>
                  <TableCell>{formatDate(subscriber.createdAt)}</TableCell>
                  <TableCell>{formatDate(subscriber.lastActivity)}</TableCell>
                  <TableCell align="center">
                    <Tooltip title="Удалить">
                      <IconButton 
                        size="small"
                        onClick={() => handleDeleteClick(subscriber)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Управление подписчиками
        </Typography>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={fetchSubscribers}
        >
          Обновить
        </Button>
      </Box>

      <Tabs 
        value={tabValue} 
        onChange={handleTabChange} 
        sx={{ mb: 3 }}
        variant="fullWidth"
      >
        <Tab 
          icon={<EmailIcon />} 
          iconPosition="start" 
          label="Email подписчики" 
        />
        <Tab 
          icon={<TelegramIcon />} 
          iconPosition="start" 
          label="Telegram подписчики" 
        />
      </Tabs>

      <Box sx={{ mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={4}>
            <TextField
              label="Поиск"
              variant="outlined"
              fullWidth
              size="small"
              value={searchQuery}
              onChange={handleSearchQueryChange}
              placeholder={tabValue === 0 ? "Поиск по email или имени" : "Поиск по имени или username"}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              select
              label="Статус"
              variant="outlined"
              fullWidth
              size="small"
              value={statusFilter}
              onChange={handleStatusFilterChange}
            >
              <MenuItem value="all">Все статусы</MenuItem>
              <MenuItem value="active">Активные</MenuItem>
              <MenuItem value="inactive">Неактивные</MenuItem>
            </TextField>
          </Grid>
        </Grid>
      </Box>

      {error && (
        <Box sx={{ mb: 2 }}>
          <Typography color="error">{error}</Typography>
        </Box>
      )}

      <Paper sx={{ width: '100%', overflow: 'hidden' }}>
        {tabValue === 0 ? renderEmailSubscribersTable() : renderTelegramSubscribersTable()}
        
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Строк на странице:"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} из ${count}`}
        />
      </Paper>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Подтверждение удаления</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {tabValue === 0 ? (
              `Вы действительно хотите удалить подписчика с email "${subscriberToDelete?.email}"?`
            ) : (
              `Вы действительно хотите удалить подписчика Telegram "${subscriberToDelete?.firstName || ''} ${subscriberToDelete?.lastName || ''} (${subscriberToDelete?.username || 'без имени пользователя'})"?`
            )}
            Это действие нельзя отменить.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Отмена</Button>
          <Button onClick={handleDeleteConfirm} color="error" autoFocus>
            Удалить
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SubscribersList;
